import React from 'react';
import { Chip, type ChipProps } from '@mui/material';

/**
 * Claim-class chip shared by the pricing surfaces (calculator, planner, M0
 * gallery): `observed` = provider/served truth (success), `scenario` /
 * `prior-based` = model or benchmark (warning border).
 *
 * The outlined+warning contrast fix (review B-1, M0 gallery, 2026-09-04) now
 * lives at the theme level: `ThemeModeContext.jsx → MuiChip.styleOverrides.label`
 * reads every outlined+warning chip's label in `text.secondary` in light mode
 * (7.46:1) and leaves dark mode untouched (7.84:1). ClaimChip only carries the
 * tone vocabulary (`observed`/`scenario` → success/warning color mapping).
 */
export type ClaimTone = 'observed' | 'scenario';

export interface ClaimChipProps extends Omit<ChipProps, 'color' | 'variant'> {
  tone: ClaimTone;
}

/**
 * `forwardRef`, not a plain function component: a `ClaimChip` is a natural
 * `Tooltip` child (the gallery's `prior-based` chip carries the benchmark-book
 * sentence that way), and MUI's `Tooltip` hands its child a ref. A function
 * component swallowed it and React logged two errors on every render of the
 * Pricing tab — «Invalid prop `children` supplied to ForwardRef(Tooltip2)» and
 * «Function components cannot be given refs» — while the tooltip silently never
 * positioned itself. The ref and every prop the tooltip injects go to the same
 * root element as the tone.
 */
export const ClaimChip = React.forwardRef<HTMLDivElement, ClaimChipProps>(
  ({ tone, sx, ...rest }, ref) => (
    <Chip
      ref={ref}
      size="small"
      variant="outlined"
      color={tone === 'observed' ? 'success' : 'warning'}
      sx={sx}
      {...rest}
    />
  ),
);

ClaimChip.displayName = 'ClaimChip';
